import React, { forwardRef } from 'react';
import { Shield, AlertCircle, CheckCircle2, Check, Plus, AlertTriangle } from 'lucide-react';
import { BureauLogo } from '../ui/BureauLogo';

const isPlaceholderScore = (score) => score == null || score === 0 || score === 300;

// Score status helper
const getScoreStatus = (score) => {
  if (isPlaceholderScore(score)) return { label: 'No Score', color: '#6B7280' };
  if (score >= 750) return { label: 'Excellent', color: '#10B981' };
  if (score >= 700) return { label: 'Good', color: '#22C55E' };
  if (score >= 650) return { label: 'Fair', color: '#EAB308' };
  if (score >= 500) return { label: 'Poor', color: '#F97316' };
  return { label: 'Very Poor', color: '#EF4444' };
};

// Mini Score Gauge for snapshot
const MiniScoreGauge = ({ score, size = 80 }) => {
  const resolvedScore = isPlaceholderScore(score) ? 300 : score;
  const status = getScoreStatus(score);
  const percentage = Math.max(0, Math.min(100, ((resolvedScore - 300) / 550) * 100));
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size / 2 + 10 }}>
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke={status.color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
    </div>
  );
};

// Bureau Score Card for Snapshot
const SnapshotBureauCard = ({ bureau, score, lastUpdated, reportDateLabel }) => {
  const status = getScoreStatus(score);
  const displayScore = isPlaceholderScore(score) ? '--' : score;

  return (
    <div
      className="flex flex-col items-center p-3 rounded-2xl"
      style={{
        background: 'rgba(255, 255, 255, 0.85)',
        border: '1px solid rgba(16, 185, 129, 0.15)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
      }}
    >
      <div className="h-4 flex items-center mb-1">
        <BureauLogo bureau={bureau} height="1rem" maxWidth="80px" />
      </div>

      {reportDateLabel && (
        <span
          className="text-[9px] font-bold px-2 py-0.5 rounded-full mb-1 whitespace-nowrap"
          style={{
            background: 'rgba(11, 140, 88, 0.1)',
            color: '#0B8C58',
          }}
        >
          {reportDateLabel}
        </span>
      )}

      <MiniScoreGauge score={score} size={70} />

      <p className="text-2xl font-bold text-gray-900 -mt-1">
        {displayScore}
      </p>

      <span
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
        style={{
          background: `${status.color}15`,
          color: status.color,
        }}
      >
        {status.label}
      </span>

      <p className="text-[8px] text-gray-400 mt-1">
        Updated {lastUpdated}
      </p>
    </div>
  );
};

// KPI Metric Card for Snapshot
const SnapshotMetricCard = ({ icon: Icon, label, value, color }) => (
  <div
    className="flex flex-col items-center p-2 rounded-xl"
    style={{
      background: 'rgba(255, 255, 255, 0.75)',
      border: '1px solid rgba(0,0,0,0.06)',
    }}
  >
    <div
      className="w-7 h-7 rounded-lg flex items-center justify-center mb-1"
      style={{ background: `${color}15` }}
    >
      <Icon size={14} style={{ color }} />
    </div>
    <p className="text-[9px] text-gray-500 text-center leading-tight">{label}</p>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

/**
 * Credit Results Snapshot Component
 * A premium branded snapshot for social sharing
 */
const CreditResultsSnapshot = forwardRef(({
  userData,
  scores,
  metrics,
  privacyMode = false,
  lastUpdated,
}, ref) => {
  const displayName = privacyMode
    ? (userData?.name?.split(' ')[0] + ' ' + (userData?.name?.split(' ')[1]?.[0] || '') + '.').trim()
    : userData?.name || 'FundElite Member';

  const formattedDate = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const bureauReportDate = formattedDate;

  return (
    <div
      ref={ref}
      className="snapshot-container"
      style={{
        width: '360px',
        minHeight: '450px',
        height: 'auto',
        background: 'linear-gradient(145deg, #ECFDF5 0%, #D1FAE5 50%, #A7F3D0 100%)',
        borderRadius: '24px',
        padding: '16px',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
        overflow: 'visible',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(5,150,105,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
        }}
      />

      <div className="relative z-10 flex flex-col">
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs"
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
              }}
            >
              FE
            </div>
            <span className="text-base font-bold text-emerald-800">FundElite</span>
          </div>

          <h1
            className="text-lg font-bold mb-0.5"
            style={{ color: '#065F46' }}
          >
            Credit Results Snapshot
          </h1>

          <p className="text-sm text-emerald-700 font-medium">{displayName}</p>
          <p className="text-xs text-emerald-600/70">{formattedDate}</p>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <SnapshotBureauCard
            bureau="Equifax"
            score={scores?.equifax}
            lastUpdated={formattedDate}
            reportDateLabel={scores?.equifaxFirstReport ? bureauReportDate : null}
          />
          <SnapshotBureauCard
            bureau="Experian"
            score={scores?.experian}
            lastUpdated={formattedDate}
            reportDateLabel={scores?.experianFirstReport ? bureauReportDate : null}
          />
          <SnapshotBureauCard
            bureau="TransUnion"
            score={scores?.transunion}
            lastUpdated={formattedDate}
            reportDateLabel={scores?.transunionFirstReport ? bureauReportDate : null}
          />
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-2">
          <SnapshotMetricCard
            icon={AlertCircle}
            label="Initial Negative"
            value={metrics?.initialNegative ?? '—'}
            color="#EF4444"
          />
          <SnapshotMetricCard
            icon={CheckCircle2}
            label="Initial Positive"
            value={metrics?.initialPositive ?? '—'}
            color="#EAB308"
          />
          <SnapshotMetricCard
            icon={Shield}
            label="Verified & Updated"
            value={metrics?.verifiedUpdated ?? '—'}
            color="#EAB308"
          />
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          <SnapshotMetricCard
            icon={Check}
            label="Removed/Repaired"
            value={metrics?.removedRepaired ?? '—'}
            color="#10B981"
          />
          <SnapshotMetricCard
            icon={Plus}
            label="Recently Added"
            value={metrics?.recentlyAdded ?? '—'}
            color="#EAB308"
          />
          <SnapshotMetricCard
            icon={AlertTriangle}
            label="Remaining Items"
            value={metrics?.remainingItems ?? '—'}
            color="#F97316"
          />
        </div>

        <div className="text-center pt-2 border-t border-emerald-200/50">
          <p className="text-[10px] text-emerald-700 font-medium">
            Powered by FundElite
          </p>
          <p className="text-[8px] text-emerald-600/60">
            For educational and informational use
          </p>
        </div>
      </div>
    </div>
  );
});

CreditResultsSnapshot.displayName = 'CreditResultsSnapshot';

export default CreditResultsSnapshot;
